/**
 * @file ModernPendingChangesPanel.tsx
 * @description This file contains the ModernPendingChangesPanel component. It
 * is a re-skin of the PendingChangesPanel component.
 */

import React, { useMemo } from "react";
import * as R from "ramda";

import { StateSetter } from "../../utils/types";
import { MdClose } from "react-icons/md";
import { PendingChanges, RowType } from "./modernPendingChangesUtils";
import { Header } from "../../Components/StickyTable";
import ModernPendingEditedRow from "./ModernPendingEditedRow";
import PendingNewRow from "./ModernPendingNewRow";

import "./ModernPendingChangesPanel.scss";
import PendingDeletedRow from "./ModernPendingDeletedRow";

interface PendingChangesPanelProps<R> {
  originalData?: Record<string, R>;
  pendingChanges: PendingChanges<R>;
  showPendingChanges: boolean;
  setShowPendingChanges: StateSetter<boolean>;
  headers: Header[];
}

export const ModernPendingChangesPanel = <R extends RowType>({
  originalData,
  pendingChanges,
  showPendingChanges,
  setShowPendingChanges,
  headers,
}: PendingChangesPanelProps<R>): JSX.Element => {
  const { editedRows, newRows, deletedRows } = pendingChanges;

  const hasPendingChanges = useMemo(
    () => !R.isEmpty(editedRows) || !R.isEmpty(newRows) || !R.isEmpty(deletedRows),
    [editedRows, newRows, deletedRows]
  );

  return showPendingChanges && hasPendingChanges ? (
    <div className="modernPendingChangesPanelContainer">
      <div className="modernPendingChangesPanelHeader">
        Changes
        <div
          className="modernPendingChangesPanelCloseButton"
          onClick={() => setShowPendingChanges(false)}
        >
          <MdClose />
        </div>
      </div>
      <div className="modernPendingChangesPanelBody">
        {editedRows && !R.isEmpty(editedRows) && originalData && (
          <div className="modernPendingChangesEditedSection">
            <span className="modernPendingChangesSectionTitle">Edited Rows</span>
            <div className="modernPendingChangesList">
              {editedRows.map(editedRow => {
                return (
                  <ModernPendingEditedRow
                    key={JSON.stringify(editedRow)}
                    oldRow={originalData[editedRow.id]}
                    newRow={editedRow}
                    columns={headers}
                  />
                );
              })}
            </div>
          </div>
        )}
        {newRows && !R.isEmpty(newRows) && (
          <div className="modernPendingChangesNewSection">
            <span className="modernPendingChangesSectionTitle">New Rows</span>
            <div className="modernPendingChangesList">
              {newRows.map(newRow => {
                return (
                  <PendingNewRow key={JSON.stringify(newRow)} newRow={newRow} columns={headers} />
                );
              })}
            </div>
          </div>
        )}
        {deletedRows && !R.isEmpty(deletedRows) && (
          <div className="modernPendingChangesDeletedSection">
            <span className="modernPendingChangesSectionTitle">Deleted Rows</span>
            <div className="modernPendingChangesList">
              {deletedRows.map(deletedRow => {
                return (
                  <PendingDeletedRow
                    key={JSON.stringify(deletedRow)}
                    oldRow={deletedRow}
                    columns={headers}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div></div>
  );
};
